import React from "react";
import { useFragment, useRelayEnvironment } from "react-relay";

import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { graphql } from "relay-runtime";
import uuid from "uuid";

import { useID } from "~/lib/hooks";
import { commitMutationPromise, useLazyLoadQuery } from "~/lib/relayUtil";
import { useModals } from "~/ui";
import { ButtonLink, HelpCenterIcon, HelpCenterLink, Switch } from "~/ui/legacyFellowIndex";

import OutOfCreditsModal from "../../NoteHeader/OutOfCreditsModal";

import type { StreamAutoRecordAutomationConfig_useAutoRecordReason_stream$key } from "~/__generated__/StreamAutoRecordAutomationConfig_useAutoRecordReason_stream.graphql";
import type { StreamAutoRecordAutomationConfig_useAutoRecordReason_viewer$key } from "~/__generated__/StreamAutoRecordAutomationConfig_useAutoRecordReason_viewer.graphql";
import type { StreamAutoRecordAutomationConfig_viewer$key } from "~/__generated__/StreamAutoRecordAutomationConfig_viewer.graphql";
import type { StreamAutoRecordSetting } from "~/__generated__/StreamAutoRecordAutomationConfigMutation.graphql";
import type { StreamAutoRecordAutomationConfigQuery } from "~/__generated__/StreamAutoRecordAutomationConfigQuery.graphql";

import stylesSettings from "~/settings/css/settings.module.css";
import stylesStreamSettings from "~/stream/StreamSettings/StreamSettings.module.css";

import automationStyles from "./Automations.module.css";

const query = graphql`
	query StreamAutoRecordAutomationConfigQuery($streamID: ID!) {
		node(id: $streamID) {
			...StreamAutoRecordAutomationConfig_useAutoRecordReason_stream
			... on NoteStream {
				id
				autoRecordSetting {
					__typename
					... on NoteStreamStreamBasedAutoRecordSource {
						value
						creator {
							id
							firstName
							fullName
						}
					}
					... on NoteStreamUserSettingsBasedAutoRecordEnableSource {
						users {
							id
							firstName
							fullName
						}
						rules {
							ruleLevel
							autoRecordRuleUsers {
								user {
									id
									firstName
									fullName
								}
							}
						}
						cannotBeRecordedReason
					}
				}
			}
		}
	}
`;

const mutation = graphql`
	mutation StreamAutoRecordAutomationConfigMutation($value: StreamAutoRecordSetting!, $streamID: ID!) {
		updateStreamAutoRecordSettings(streamId: $streamID, value: $value) {
			stream {
				id
				autoRecordSetting {
					__typename
					... on NoteStreamStreamBasedAutoRecordSource {
						value
						creator {
							id
							firstName
							fullName
						}
					}
					... on NoteStreamUserSettingsBasedAutoRecordEnableSource {
						users {
							id
							firstName
							fullName
						}
						rules {
							ruleLevel
							autoRecordRuleUsers {
								user {
									id
									firstName
									fullName
								}
							}
						}
						cannotBeRecordedReason
					}
				}
				meetingRecapAutoShareSource {
					__typename
					... on UserMeetingRecapAutoShareSettings {
						id
						sendTo_U: sendTo
						user {
							id
							fullName
							firstName
						}
					}
				}
				schedulingMeetingAssistantAutomationIsActive
				automationConfigurations {
					...AutomationsConfigurationsData @relay(mask: false)
				}
				claimedEvents {
					willBeRecorded
				}
			}
		}
	}
`;

export const getName = (user: { firstName: string | null; fullName: string | null; isViewer: boolean } | null) => {
	if (!user) return "Unknown";
	if (user.isViewer) return "you";
	return user.firstName || user.fullName || "Unknown";
};

export function useAutoRecordReason(
	streamKey: StreamAutoRecordAutomationConfig_useAutoRecordReason_stream$key | null,
	viewerKey: StreamAutoRecordAutomationConfig_useAutoRecordReason_viewer$key,
): string | undefined {
	const viewer = useFragment(
		graphql`
			fragment StreamAutoRecordAutomationConfig_useAutoRecordReason_viewer on Viewer {
				hasAccessToCopilot
			}
		`,
		viewerKey,
	);

	const stream = useFragment<StreamAutoRecordAutomationConfig_useAutoRecordReason_stream$key>(
		graphql`
			fragment StreamAutoRecordAutomationConfig_useAutoRecordReason_stream on NoteStream {
				autoRecordSetting {
					__typename
					... on NoteStreamStreamBasedAutoRecordSource {
						value
						creator {
							id
							firstName
							fullName
							isViewer
						}
					}
					... on NoteStreamUserSettingsBasedAutoRecordEnableSource {
						users {
							id
							firstName
							fullName
							isViewer
						}
						rules {
							ruleLevel
							autoRecordRuleUsers {
								user {
									id
									firstName
									fullName
									isViewer
								}
							}
						}
						cannotBeRecordedReason
					}
				}
			}
		`,
		streamKey,
	);
	const disabledByAccount = !viewer.hasAccessToCopilot;
	if (disabledByAccount) return "This feature has been disabled by your workspace admin";

	if (!stream) return undefined;
	if (!stream.autoRecordSetting) return undefined;

	const { __typename } = stream.autoRecordSetting;

	if (__typename === "NoteStreamStreamBasedAutoRecordSource") {
		const { value, creator } = stream.autoRecordSetting;

		const action = value ? "on" : "off";

		const creatorName = getName(creator);
		return creator
			? `Auto-record was turned ${action} by ${creator.isViewer ? "you" : creatorName}`
			: `Auto-record was turned ${action} for this stream`;
	}

	if (__typename === "NoteStreamUserSettingsBasedAutoRecordEnableSource") {
		const { cannotBeRecordedReason, users, rules } = stream.autoRecordSetting;
		if (cannotBeRecordedReason) return cannotBeRecordedReason;

		const numWorkspaceRules = rules.filter(rule => rule.ruleLevel !== "USER").length;
		const numUsers = users.length;

		if (numWorkspaceRules > 0 || numUsers > 0) {
			let reason = "Turned on by ";
			if (numWorkspaceRules > 0) {
				// eslint-disable-next-line max-len
				reason += `${numWorkspaceRules} workspace auto-record setting${numWorkspaceRules > 1 ? "s" : ""} applying to invitees`;
			}
			if (numWorkspaceRules > 0 && numUsers > 0) reason += ", and ";
			if (numUsers > 0) {
				reason += "the user settings of ";
				reason += users.map(user => getName(user)).join(", ");
			}
			return reason;
		}
		return "Using default user settings";
	}

	return undefined;
}

type Props = {
	streamID: string;
	afterSave?: () => void;
	viewer: StreamAutoRecordAutomationConfig_viewer$key;
};

export function StreamAutoRecordAutomationConfig({ streamID, afterSave, viewer: viewerKey }: Props) {
	const viewer = useFragment(
		graphql`
			fragment StreamAutoRecordAutomationConfig_viewer on Viewer {
				remainingMeetingRecordingCredits
				hasAccessToCopilot

				...StreamAutoRecordAutomationConfig_useAutoRecordReason_viewer
			}
		`,
		viewerKey,
	);
	const { remainingMeetingRecordingCredits } = viewer;
	const disabledByAccount = !viewer.hasAccessToCopilot;

	const toggleID = useID();
	const [fetchKey] = React.useState(() => uuid.v4());
	const data = useLazyLoadQuery<StreamAutoRecordAutomationConfigQuery>(
		query,
		{ streamID },
		{ fetchPolicy: "store-and-network", fetchKey },
	);
	const modals = useModals();
	const showOutOfCreditsModal = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			modals.open(({ close }) => <OutOfCreditsModal onClose={close} />);
		},
		[modals],
	);
	const settings = data.node?.autoRecordSetting;
	const isOn = React.useMemo(() => {
		if (!settings) return false;
		if (settings.__typename === "NoteStreamStreamBasedAutoRecordSource") return settings.value;
		if (settings.__typename === "NoteStreamUserSettingsBasedAutoRecordEnableSource") {
			return !settings.cannotBeRecordedReason && (settings.users.length > 0 || settings.rules.length > 0);
		}
		return false;
	}, [settings]);

	const disabledMessage = React.useMemo(() => {
		if (!settings) return undefined;
		if (settings.__typename === "NoteStreamUserSettingsBasedAutoRecordEnableSource")
			return settings.cannotBeRecordedReason;
		if (disabledByAccount) return "This feature has been disabled by your workspace admin";
		return undefined;
	}, [settings, disabledByAccount]);

	const environment = useRelayEnvironment();
	const updateStreamAutoRecordSettings = React.useCallback(
		(value: StreamAutoRecordSetting) =>
			commitMutationPromise(environment, { mutation, variables: { value, streamID } }),
		[environment, streamID],
	);
	const setToRespectUser = React.useCallback(
		() => updateStreamAutoRecordSettings("RESPECT_USER_SETTINGS").then(afterSave),
		[updateStreamAutoRecordSettings, afterSave],
	);
	const setToOn = React.useCallback(
		() => updateStreamAutoRecordSettings("ENABLED_BY_STREAM_AUTOMATION").then(afterSave),
		[updateStreamAutoRecordSettings, afterSave],
	);
	const setToOff = React.useCallback(
		() => updateStreamAutoRecordSettings("FORCE_DISABLED").then(afterSave),
		[updateStreamAutoRecordSettings, afterSave],
	);
	const onToggleChange = React.useCallback(
		(checked: boolean) => (checked ? setToOn() : setToOff()),
		[setToOn, setToOff],
	);

	const autoRecordReason = useAutoRecordReason(data.node, viewer);

	return (
		<section className={automationStyles.automationBlock}>
			<div className={automationStyles.row}>
				<h1 className={automationStyles.title}>
					Auto-record
					<HelpCenterIcon articleId="8589106" title="Note Taker" className={automationStyles.headerHelpIcon} />
				</h1>
				{remainingMeetingRecordingCredits === 0 && (
					<div
						className={automationStyles.outOfCredits}
						onClick={showOutOfCreditsModal}
						data-test-id="stream-settings-automations-ooc"
					>
						<FontAwesomeIcon icon={faArrowAltCircleUp} className={automationStyles.outOfCreditUpgrade} />
						<div>Out of recording credits</div>
					</div>
				)}
			</div>
			<div className={automationStyles.row}>
				<label htmlFor={toggleID} className={automationStyles.label}>
					The Note Taker will automatically join, record, and generate a Recap for meetings in this series.
				</label>
				<Switch
					id={toggleID}
					checked={isOn}
					data-disabled={disabledMessage ? true : undefined}
					disabled={disabledMessage != null}
					disabledTooltip={disabledMessage ?? undefined}
					tooltipPlacement="top"
					onCheckedChange={onToggleChange}
					className={cx(stylesStreamSettings.toggle, stylesSettings.settingsPageToggle)}
				/>
			</div>
			<div className={automationStyles.row} style={{ font: "var(--paragraph-5)", color: "var(--secondaryTextColor)" }}>
				{disabledMessage ? (
					<div>{disabledMessage}</div>
				) : (
					<div>
						{autoRecordReason}
						{settings?.__typename === "NoteStreamStreamBasedAutoRecordSource" && (
							<>
								<br />
								<ButtonLink onClick={setToRespectUser}>Reset to use default user settings.</ButtonLink>
							</>
						)}
						{settings?.__typename === "NoteStreamUserSettingsBasedAutoRecordEnableSource" && !isOn && (
							<>
								{" "}
								<HelpCenterLink articleId="8589106">Learn more.</HelpCenterLink>{" "}
							</>
						)}
					</div>
				)}
			</div>
		</section>
	);
}
